/**
 * Session Tracker for Prism Builder
 * Tracks user session from entry to add-to-cart via New Relic
 */
class SessionTracker {
  constructor() {
    this.sessionData = null;
    this.startTime = null;
  }

  /**
   * Start tracking a new session
   * @param {Object} sessionData - Initial session data
   */
  startSession(sessionData) {
    if (this.isSessionActive()) {
      console.warn('Session already active, ignoring startSession');
      return;
    }
    this.startTime = Date.now();
    this.sessionData = {
      // Product information
      productType: sessionData.productType,
      productId: sessionData.productId,
      // productTitle matches the facet used by the Prism_hasLowDpi diagnostics,
      // so the lifecycle metrics can be sliced by the same product key.
      productTitle: sessionData.productTitle || window.wkData?.product_title,
      productBreadcrumbs: sessionData.productBreadcrumbs,

      // Initial state
      initialQuantity: sessionData.initialQuantity,
      initialPageCount: sessionData.initialPageCount,
      inCart: sessionData.inCart,

      // Tracking metrics (will be updated during session)
      // sourceFilesUploaded: distinct files the user chose (a multi-page PDF counts once)
      // imagesCreated: backend image objects produced (each PDF page counts separately)
      sourceFilesUploaded: 0,
      imagesCreated: 0,
      countedUploadBatches: new Set(),
      // Byte sizes of the images uploaded this session. "Sent" is what went
      // over the wire after in-browser compression; "picked" is the file the
      // user chose, only known for image files (not PDF pages).
      uploadSizes: {
        sentCount: 0,
        sentTotal: 0,
        sentMax: 0,
        pickedCount: 0,
        pickedTotal: 0,
        pickedMax: 0,
      },
      // Per-slot low-DPI lifecycle, keyed by slot_id. Tracks whether a slot ever
      // held a low-DPI image, whether it was later improved, and how.
      lowDpiSlots: {},
      // Per-slot good-DPI adds, keyed by slot_id. Only used to de-dupe the
      // high-frequency draw-loop evaluations into one "good add" per distinct
      // image, so the dashboard has a denominator for the low-vs-good split.
      goodDpiSlots: {},
      pagesAdded: 0,
      pagesRemoved: 0,
      themeChanges: 0,
      previewViewed: false,
      svgTextEdits: 0,
      svgTextFeatureEnabled: !!(
        window?.featureFlags?.builder_svg_text && window.wkData?.enable_svg_text
      ),
    };

    // Send session start event to New Relic
    this._sendToNewRelic('prism_builder_session_start', {
      product_type: this.sessionData.productType,
      product_id: this.sessionData.productId,
      product_title: this.sessionData.productTitle,
      breadcrumbs: this.sessionData.productBreadcrumbs,
      initial_quantity: this.sessionData.initialQuantity,
      initial_page_count: this.sessionData.initialPageCount,
      in_cart: this.sessionData.inCart,
    });

    this._armAbandonmentTracking();
  }

  /**
   * Arm a best-effort abandonment handler: if the page is hidden/closed while a
   * session is still active (add-to-cart never ended it), end it as 'abandoned'
   * so fall-off — including DPI-correlated fall-off — is measurable.
   *
   * Best-effort by nature: addPageAction on page hide may not always flush
   * before the page is torn down. It is a directional signal, not exact.
   * @private
   */
  _armAbandonmentTracking() {
    if (typeof window === 'undefined' || this._abandonmentArmed) return;
    this._abandonmentArmed = true;

    this._onPageHide = () => {
      if (!this.isSessionActive()) return;
      this.endSession({ outcome: 'abandoned' });
    };
    // pagehide covers tab close / navigation; visibilitychange→hidden covers
    // mobile backgrounding, which often doesn't fire pagehide.
    window.addEventListener('pagehide', this._onPageHide);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this._onPageHide();
    });
  }

  /**
   * Track an upload batch during the session.
   *
   * Counting is idempotent per batch: a concurrency race, a re-fired
   * UPLOAD_DONE, or an album re-handoff on reload all carry the same
   * batchId, so the batch is only counted once.
   *
   * @param {Object} batch
   * @param {string} batch.batchId - Stable id for this upload action
   * @param {number} batch.sourceFiles - Files the user chose (PDF counts as 1)
   * @param {number} batch.imagesCreated - Backend images produced (PDF pages count separately)
   */
  trackUploadBatch({ batchId, sourceFiles, imagesCreated }) {
    if (!this.sessionData || !batchId) return;
    if (this.sessionData.countedUploadBatches.has(batchId)) return;

    this.sessionData.countedUploadBatches.add(batchId);
    this.sessionData.sourceFilesUploaded += sourceFiles || 0;
    this.sessionData.imagesCreated += imagesCreated || 0;
  }

  /**
   * Track the byte size of one image uploaded during the session.
   *
   * @param {Object} image
   * @param {number} image.sentBytes - Bytes uploaded after in-browser compression
   * @param {number} [image.pickedBytes] - Size of the file the user chose; omit
   *   when it isn't a per-image size (e.g. the PDF behind a rendered page)
   */
  trackUploadedImage({ sentBytes, pickedBytes }) {
    if (!this.sessionData) return;
    const sizes = this.sessionData.uploadSizes;

    if (Number.isFinite(sentBytes) && sentBytes > 0) {
      sizes.sentCount += 1;
      sizes.sentTotal += sentBytes;
      sizes.sentMax = Math.max(sizes.sentMax, sentBytes);
    }
    if (Number.isFinite(pickedBytes) && pickedBytes > 0) {
      sizes.pickedCount += 1;
      sizes.pickedTotal += pickedBytes;
      sizes.pickedMax = Math.max(sizes.pickedMax, pickedBytes);
    }
  }

  /**
   * Track a single DPI evaluation for a slot.
   *
   * Called for every DPI (re)computation — including from the canvas draw loop,
   * many times per second. State is keyed by slotId so the high-frequency noise
   * collapses to a per-slot lifecycle: a slot is recorded once when it first
   * holds a low-DPI image, and marked improved when a later evaluation for the
   * same slot crosses back above the threshold.
   *
   * @param {Object} evaluation
   * @param {string|number} evaluation.slotId
   * @param {string|number} [evaluation.photoId] - Current image in the slot
   * @param {number} [evaluation.dpi] - Calculated DPI
   * @param {boolean} evaluation.isLowDpi
   * @param {string} [evaluation.via] - Cause of an improvement: 'upscale' | 'other'
   * @returns {{addedLowDpi: boolean, addedGood: boolean, improved: boolean}} what state change, if any, this caused
   */
  trackDpiEvaluation({ slotId, photoId, dpi, isLowDpi, via }) {
    const noChange = { addedLowDpi: false, addedGood: false, improved: false };
    if (!this.sessionData || slotId == null) return noChange;

    const slots = this.sessionData.lowDpiSlots;
    const existing = slots[slotId];

    if (isLowDpi) {
      // Record the first time this slot holds a low-DPI image. A photo swap
      // (different photoId) re-opens the lifecycle so a freshly-added low-DPI
      // image isn't masked by an earlier improvement on the same slot.
      if (!existing || existing.photoId !== photoId) {
        slots[slotId] = {
          photoId,
          worstDpi: dpi ?? null,
          improved: false,
          improvedVia: null,
        };
        return { addedLowDpi: true, addedGood: false, improved: false };
      }
      existing.improved = false;
      existing.improvedVia = null;
      if (
        dpi != null &&
        (existing.worstDpi == null || dpi < existing.worstDpi)
      ) {
        existing.worstDpi = dpi;
      }
      return noChange;
    }

    // Not low DPI. Two sub-cases:
    // 1. This slot was previously tracked as low for the same photo (or an
    //    improvement cause is supplied) — mark it improved once.
    if (existing && !existing.improved) {
      existing.improved = true;
      existing.improvedVia = via || existing.improvedVia || 'other';
      existing.finalDpi = dpi ?? existing.finalDpi ?? null;
      return { addedLowDpi: false, addedGood: false, improved: true };
    }

    // 2. A good image newly placed in a slot that was never low. Tracked in a
    //    separate per-slot map (keyed slot+photo) purely so the high-frequency
    //    draw-loop evaluations collapse to one "good add" per distinct image —
    //    this gives the denominator for "% of added images that are low-DPI".
    //    Does not touch lowDpiSlots, so the low-DPI lifecycle summary is
    //    unaffected.
    if (!existing) {
      const goodSlots = this.sessionData.goodDpiSlots;
      if (goodSlots[slotId]?.photoId !== photoId) {
        goodSlots[slotId] = { photoId };
        return { addedLowDpi: false, addedGood: true, improved: false };
      }
    }
    return noChange;
  }

  /**
   * Derive the low-DPI summary for the current session.
   * @private
   */
  _dpiSummary() {
    const slots = Object.values(this.sessionData.lowDpiSlots);
    const added = slots.length;
    const improved = slots.filter((s) => s.improved).length;
    const remaining = added - improved;

    // Distinct good-DPI images added this session (deduped per slot+photo in
    // trackDpiEvaluation). Gives the Lifecycle page a denominator so the
    // low-vs-good split is visible per session, not only per image.
    const goodAdded = Object.keys(this.sessionData.goodDpiSlots).length;
    const totalAdded = added + goodAdded;

    // Only 'upscale' is reliably detectable; everything else is bucketed as
    // 'other' so the two counts always reconcile with low_dpi_improved_count.
    const improvedVia = { upscale: 0, other: 0 };
    let worstDpi = null;
    for (const s of slots) {
      if (s.improved) {
        improvedVia[s.improvedVia === 'upscale' ? 'upscale' : 'other'] += 1;
      }
      if (s.worstDpi != null && (worstDpi == null || s.worstDpi < worstDpi)) {
        worstDpi = s.worstDpi;
      }
    }

    return {
      low_dpi_added_count: added,
      low_dpi_improved_count: improved,
      low_dpi_remaining_count: remaining,
      low_dpi_worst_dpi: worstDpi,
      low_dpi_improved_via_upscale: improvedVia.upscale,
      low_dpi_improved_via_other: improvedVia.other,
      // Good adds + total adds: low_dpi_added_count / total_images_added is the
      // per-session share of added images that were low-DPI.
      good_dpi_added_count: goodAdded,
      total_images_added: totalAdded,
    };
  }

  /**
   * Track page addition
   */
  trackPageAdded() {
    if (this.sessionData) {
      this.sessionData.pagesAdded += 1;
    }
  }

  /**
   * Track page removal
   */
  trackPageRemoved() {
    if (this.sessionData) {
      this.sessionData.pagesRemoved += 1;
    }
  }

  /**
   * Track theme/design change
   */
  trackThemeChange() {
    if (this.sessionData) {
      this.sessionData.themeChanges += 1;
    }
  }

  /**
   * Track preview view
   */
  trackPreviewViewed() {
    if (this.sessionData && !this.sessionData.previewViewed) {
      this.sessionData.previewViewed = true;
    }
  }

  /**
   * Track SVG text slot edit confirmed
   */
  trackSvgTextEdit() {
    if (this.sessionData) {
      this.sessionData.svgTextEdits += 1;
      return this.sessionData.svgTextEdits === 1;
    }
    return false;
  }

  /**
   * End session and send comprehensive analytics
   * @param {Object} endData - Final session data
   */
  endSession(endData) {
    if (!this.sessionData || !this.startTime) {
      console.warn('Session not started or already ended');
      return;
    }

    const sessionDuration = Math.round((Date.now() - this.startTime) / 1000); // in seconds

    // Calculate engagement metrics
    const totalSlots = endData.totalPhotoSlots || 0;
    const filledSlots = endData.filledPhotoSlots || 0;
    const slotFillRate =
      totalSlots > 0 ? ((filledSlots / totalSlots) * 100).toFixed(2) : 0;

    // Send session end event to New Relic with all data
    this._sendToNewRelic('prism_builder_session_end', {
      // Session metadata
      session_duration_seconds: sessionDuration,
      session_duration_minutes: parseFloat((sessionDuration / 60).toFixed(2)),

      // Product information
      product_type: this.sessionData.productType,
      product_id: this.sessionData.productId,
      product_title: this.sessionData.productTitle,
      breadcrumbs: this.sessionData.productBreadcrumbs,

      // Final state
      final_quantity: endData.finalQuantity,
      final_page_count: endData.finalPageCount,
      final_price: endData.finalPrice,

      // Engagement metrics
      // source_files_uploaded: distinct files the user picked (PDF = 1)
      // images_created: backend images produced (each PDF page counted)
      // photos_uploaded: legacy field, kept = images_created for dashboard back-compat
      source_files_uploaded: this.sessionData.sourceFilesUploaded,
      images_created: this.sessionData.imagesCreated,
      photos_uploaded: this.sessionData.imagesCreated,
      ...this._uploadSizeSummary(),
      pages_added: this.sessionData.pagesAdded,
      pages_removed: this.sessionData.pagesRemoved,
      theme_changes: this.sessionData.themeChanges,
      preview_viewed: this.sessionData.previewViewed,

      // Photo slot metrics
      total_photo_slots: totalSlots,
      filled_photo_slots: filledSlots,
      slot_fill_rate_percent: parseFloat(slotFillRate),

      // SVG text feature metrics
      svg_text_feature_enabled: this.sessionData.svgTextFeatureEnabled,
      svg_text_used: this.sessionData.svgTextEdits > 0,
      svg_text_edits: this.sessionData.svgTextEdits,
      svg_text_slot_count: endData.svgTextSlotCount || 0,

      // Outcome: 'added_to_cart' (explicit add-to-cart) or 'abandoned'
      // (left without adding to cart — best-effort, captured on page hide).
      session_outcome: endData.outcome || 'added_to_cart',

      // Low-DPI lifecycle: how many slots ever held a low-DPI image, how many
      // were improved (and via what), how many still low at session end, and
      // whether the user acknowledged the warning and proceeded to cart anyway.
      ...this._dpiSummary(),
      low_dpi_acknowledged: !!endData.lowDpiAcknowledged,
    });

    // Reset session
    this.sessionData = null;
    this.startTime = null;
  }

  /**
   * Upload byte sizes for the session end event. Averages and maxima are left
   * out when nothing was measured, so sessions without uploads don't pull
   * cross-session averages towards zero; totals and counts are always sent so
   * sum(total) / sum(count) gives a per-image average across sessions.
   * @private
   */
  _uploadSizeSummary() {
    const sizes = this.sessionData.uploadSizes;
    const summary = {
      uploaded_images_sized: sizes.sentCount,
      upload_sent_bytes_total: sizes.sentTotal,
      upload_picked_bytes_total: sizes.pickedTotal,
      upload_picked_images_sized: sizes.pickedCount,
    };

    if (sizes.sentCount > 0) {
      summary.upload_sent_bytes_avg = Math.round(
        sizes.sentTotal / sizes.sentCount,
      );
      summary.upload_sent_bytes_max = sizes.sentMax;
    }
    if (sizes.pickedCount > 0) {
      summary.upload_picked_bytes_avg = Math.round(
        sizes.pickedTotal / sizes.pickedCount,
      );
      summary.upload_picked_bytes_max = sizes.pickedMax;
    }

    return summary;
  }

  /**
   * Get current session duration
   * @returns {number} Duration in seconds
   */
  getSessionDuration() {
    if (!this.startTime) return 0;
    return Math.round((Date.now() - this.startTime) / 1000);
  }

  /**
   * Check if session is active
   * @returns {boolean}
   */
  isSessionActive() {
    return this.sessionData !== null && this.startTime !== null;
  }

  /**
   * Send event to New Relic
   * @private
   */
  _sendToNewRelic(actionName, data) {
    try {
      if (window.newrelic) {
        window.newrelic.addPageAction(actionName, data);
      }
    } catch (error) {
      console.error('Failed to send session tracking to New Relic:', error);
    }
  }
}

// Export singleton instance
export const sessionTracker = new SessionTracker();
